import React, { useState, useEffect } from 'react';
import { X, Circle, Trophy, Users, Copy, Check } from 'lucide-react';

const SuperTicTacToe = () => {
	const [socket, setSocket] = useState(null);
	const [gameState, setGameState] = useState({
		bigBoard: Array(9).fill(null),
		smallBoards: Array(9)
			.fill(null)
			.map(() => Array(9).fill(null)),
		currentPlayer: 'X',
		nextBoard: null,
		winner: null,
		roomId: null,
		playerId: null,
		playerSymbol: null,
		opponent: null,
	});
	const [roomInput, setRoomInput] = useState('');
	const [status, setStatus] = useState('Connecting...');
	const [copied, setCopied] = useState(false);

	useEffect(() => {
		const ws = new WebSocket('wss://super-tictactoe.jozzstack.xyz/ws/');

		ws.onopen = () => {
			setStatus('Connected. Create or join a room.');
			setSocket(ws);
		};

		ws.onmessage = (event) => {
			const data = JSON.parse(event.data);

			switch (data.type) {
				case 'roomCreated':
					setGameState((prev) => ({
						...prev,
						roomId: data.roomId,
						playerId: data.playerId,
						playerSymbol: 'X',
					}));
					setStatus(`Room created! Share code: ${data.roomId}`);
					break;

				case 'roomJoined':
					setGameState((prev) => ({
						...prev,
						roomId: data.roomId,
						playerId: data.playerId,
						playerSymbol: 'O',
						opponent: data.opponent,
					}));
					setStatus('Joined room. Game starting!');
					break;

				case 'gameStart':
					setGameState((prev) => ({
						...prev,
						opponent: data.opponent,
					}));
					setStatus('Game started! X goes first.');
					break;

				case 'gameState':
					setGameState((prev) => ({
						...prev,
						...data.state,
					}));
					updateStatus(data.state);
					break;

				case 'error':
					setStatus(`Error: ${data.message}`);
					break;

				case 'opponentLeft':
					setStatus('Opponent left the game');
					break;
			}
		};

		ws.onclose = () => {
			setStatus('Disconnected from server');
		};

		return () => ws.close();
	}, []);

	const updateStatus = (state) => {
		if (state.winner) {
			if (state.winner === 'draw') {
				setStatus("It's a draw!");
			} else {
				setStatus(`${state.winner} wins!`);
			}
		} else if (state.currentPlayer === gameState.playerSymbol) {
			setStatus('Your turn');
		} else {
			setStatus("Opponent's turn");
		}
	};

	const createRoom = () => {
		if (socket && socket.readyState === WebSocket.OPEN) {
			socket.send(JSON.stringify({ type: 'createRoom' }));
		}
	};

	const joinRoom = () => {
		if (socket && socket.readyState === WebSocket.OPEN && roomInput) {
			socket.send(JSON.stringify({ type: 'joinRoom', roomId: roomInput }));
		}
	};

	const copyRoomId = () => {
		navigator.clipboard.writeText(gameState.roomId);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	const makeMove = (bigIndex, smallIndex) => {
		if (!socket || !gameState.roomId || gameState.winner) return;
		if (gameState.currentPlayer !== gameState.playerSymbol) return;
		if (gameState.nextBoard !== null && gameState.nextBoard !== bigIndex) return;
		if (gameState.smallBoards[bigIndex][smallIndex] !== null) return;
		if (gameState.bigBoard[bigIndex] !== null) return;

		socket.send(
			JSON.stringify({
				type: 'move',
				roomId: gameState.roomId,
				bigIndex,
				smallIndex,
			})
		);
	};

	const checkWinner = (board) => {
		const lines = [
			[0, 1, 2],
			[3, 4, 5],
			[6, 7, 8],
			[0, 3, 6],
			[1, 4, 7],
			[2, 5, 8],
			[0, 4, 8],
			[2, 4, 6],
		];

		for (let line of lines) {
			const [a, b, c] = line;
			if (board[a] && board[a] === board[b] && board[a] === board[c]) {
				return board[a];
			}
		}

		return board.every((cell) => cell !== null) ? 'draw' : null;
	};

	const resetGame = () => {
		if (socket && socket.readyState === WebSocket.OPEN && gameState.roomId) {
			socket.send(
				JSON.stringify({
					type: 'reset',
					roomId: gameState.roomId,
				})
			);
		}
	};

	const SmallBoard = ({ bigIndex, board, winner }) => {
		const isActive =
			gameState.nextBoard === null || gameState.nextBoard === bigIndex;
		const isWon = gameState.bigBoard[bigIndex] !== null;

		return (
			<div
				className={`relative border-4 rounded-lg transition-all ${
					isActive && !isWon && !gameState.winner
						? 'border-blue-500 bg-blue-50'
						: 'border-gray-300 bg-white'
				} ${isWon ? 'opacity-60' : ''}`}>
				{isWon ? (
					<div className='absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 rounded-lg'>
						{gameState.bigBoard[bigIndex] === 'X' ? (
							<X className='w-20 h-20 text-blue-600' strokeWidth={3} />
						) : gameState.bigBoard[bigIndex] === 'O' ? (
							<Circle className='w-20 h-20 text-red-600' strokeWidth={3} />
						) : (
							<div className='text-3xl text-gray-500 font-bold'>Draw</div>
						)}
					</div>
				) : (
					<div className='grid grid-cols-3 gap-1 p-2'>
						{board.map((cell, smallIndex) => (
							<button
								key={smallIndex}
								onClick={() => makeMove(bigIndex, smallIndex)}
								disabled={cell !== null || !isActive || gameState.winner !== null}
								className={`w-12 h-12 border-2 border-gray-200 rounded flex items-center justify-center text-2xl font-bold transition-all ${
									cell === null && isActive && !gameState.winner
										? 'hover:bg-gray-100 cursor-pointer'
										: 'cursor-not-allowed'
								}`}>
								{cell === 'X' && (
									<X className='w-8 h-8 text-blue-600' strokeWidth={3} />
								)}
								{cell === 'O' && (
									<Circle className='w-8 h-8 text-red-600' strokeWidth={3} />
								)}
							</button>
						))}
					</div>
				)}
			</div>
		);
	};

	if (!gameState.roomId) {
		return (
			<div className='min-h-screen bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center p-4'>
				<div className='bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full'>
					<div className='text-center mb-8'>
						<Trophy className='w-16 h-16 text-yellow-500 mx-auto mb-4' />
						<h1 className='text-4xl font-bold text-gray-800 mb-2'>
							Super Tic-Tac-Toe
						</h1>
						<p className='text-gray-600'>{status}</p>
					</div>

					<div className='space-y-4'>
						<button
							onClick={createRoom}
							disabled={!socket}
							className='w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2'>
							<Users className='w-5 h-5' />
							Create New Room
						</button>

						<div className='relative'>
							<div className='absolute inset-0 flex items-center'>
								<div className='w-full border-t border-gray-300'></div>
							</div>
							<div className='relative flex justify-center text-sm'>
								<span className='px-2 bg-white text-gray-500'>OR</span>
							</div>
						</div>

						<div className='space-y-2'>
							<input
								type='text'
								value={roomInput}
								onChange={(e) => setRoomInput(e.target.value.toUpperCase())}
								placeholder='Enter room code'
								className='w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-center text-lg font-mono'
								maxLength={6}
							/>
							<button
								onClick={joinRoom}
								disabled={!socket || !roomInput}
								className='w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold py-3 px-6 rounded-lg transition-colors'>
								Join Room
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className='min-h-screen bg-gradient-to-br from-blue-100 to-purple-100 p-4'>
			<div className='max-w-6xl mx-auto'>
				<div className='bg-white rounded-2xl shadow-2xl p-6 mb-4'>
					<div className='flex justify-between items-center mb-4'>
						<div>
							<h1 className='text-3xl font-bold text-gray-800'>Super Tic-Tac-Toe</h1>
							<p className='text-gray-600'>{status}</p>
						</div>
						<div className='text-right'>
							<div className='flex items-center gap-2 mb-2'>
								<span className='text-sm text-gray-600'>Room:</span>
								<code className='bg-gray-100 px-3 py-1 rounded font-mono font-bold'>
									{gameState.roomId}
								</code>
								<button
									onClick={copyRoomId}
									className='p-1 hover:bg-gray-100 rounded transition-colors'
									title='Copy room code'>
									{copied ? (
										<Check className='w-4 h-4 text-green-600' />
									) : (
										<Copy className='w-4 h-4 text-gray-600' />
									)}
								</button>
							</div>
							<div className='text-sm'>
								<span className='font-semibold'>You: </span>
								{gameState.playerSymbol === 'X' ? (
									<X className='inline w-5 h-5 text-blue-600' />
								) : (
									<Circle className='inline w-5 h-5 text-red-600' />
								)}
							</div>
						</div>
					</div>

					{gameState.winner && (
						<div className='mb-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 flex justify-between items-center'>
							<div className='flex items-center gap-3'>
								<Trophy className='w-8 h-8 text-yellow-500' />
								<span className='text-xl font-bold'>
									{gameState.winner === 'draw'
										? "It's a draw!"
										: `${gameState.winner} wins!`}
								</span>
							</div>
							<button
								onClick={resetGame}
								className='bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition-colors'>
								New Game
							</button>
						</div>
					)}
				</div>

				<div className='bg-white rounded-2xl shadow-2xl p-6'>
					<div className='grid grid-cols-3 gap-4'>
						{gameState.smallBoards.map((board, bigIndex) => (
							<SmallBoard
								key={bigIndex}
								bigIndex={bigIndex}
								board={board}
								winner={gameState.bigBoard[bigIndex]}
							/>
						))}
					</div>
				</div>

				<div className='mt-4 bg-white rounded-lg p-4 text-sm text-gray-600'>
					<p className='font-semibold mb-2'>How to play:</p>
					<ul className='list-disc list-inside space-y-1'>
						<li>Win small boards to claim them in the big board</li>
						<li>Your move determines which board your opponent plays in next</li>
						<li>If sent to a won board, you can play anywhere</li>
						<li>Win 3 small boards in a row to win the game!</li>
					</ul>
				</div>
				<div className='mt-4  p-4 text-sm text-gray-600 text-center'>
					<p className='font-semibold mb-2'>
						Build with &#10084; by{' '}
						<span>
							<a
								href='https://www.linkedin.com/in/quince-joz-bruer/'
								target='_blank'>
								Joz-Bruer Quince
							</a>
						</span>
					</p>
				</div>
			</div>
		</div>
	);
};

export default SuperTicTacToe;
